import request from '@teetsh/app/src/utils/request';
import { serializeProgrammation } from '@teetsh/app/src/containers/Programmations/atoms/serializer';
import { Programmation } from '@teetsh/app/src/types/programmations';
import {
  progIdAtom,
  PROGRAMMATION_KEY,
} from '@teetsh/app/src/containers/Programmations/atoms/progAtoms';
import {
  addToPastAtom,
  resetFutureAtom,
} from '@teetsh/app/src/containers/Programmations/atoms/historyAtoms';
import { notifyErrorAtom } from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { atomsWithMutation, queryClientAtom } from 'jotai-tanstack-query';
import { notNullSchoolyearSchoolAtom } from '@teetsh/app/src/containers/App/context/atom';
import { AxiosError } from 'axios';
import { atom, useSetAtom } from 'jotai';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

function update({
  schoolyearId,
  schoolId,
  programmation,
}: {
  schoolyearId: string;
  schoolId: string;
  programmation: Programmation;
}) {
  const requestURL = `${
    import.meta.env.VITE_API_URL
  }/schoolyears/${schoolyearId}/schools/${schoolId}/programmations/${
    programmation.id
  }`;
  return request(requestURL, {
    method: 'PATCH',
    body: serializeProgrammation(programmation),
  });
}

const [, updateProgrammationQueryAtom] = atomsWithMutation<
  Partial<Programmation>,
  AxiosError,
  Partial<Programmation>
>(get => {
  const { schoolyearId, schoolId } = get(notNullSchoolyearSchoolAtom);
  const queryClient = get(queryClientAtom);
  const programmationId = get(progIdAtom);

  return {
    mutationFn: params => {
      const oldProg = queryClient.getQueryData<Programmation>([
        schoolyearId,
        schoolId,
        PROGRAMMATION_KEY,
        programmationId,
      ]);
      if (!oldProg) {
        return Promise.resolve() as any;
      }
      return update({
        schoolyearId,
        schoolId,
        programmation: { ...oldProg, ...params },
      });
    },
    onMutate: params => {
      queryClient.setQueryData<Programmation>(
        [schoolyearId, schoolId, PROGRAMMATION_KEY, programmationId],
        (previousProg: Programmation) => ({ ...previousProg, ...params }),
      );
    },
  };
});

const updateProgrammationAtom = (t: TFunction<['programmation']>) =>
  atom<any, [Partial<Programmation>], any>(null, async (_get, set, params) => {
    const programmationId =
      params.id === undefined ? _get(progIdAtom) : params.id;
    const programmation = { ...params, id: programmationId };
    await set(updateProgrammationQueryAtom, [programmation]);
    const result = await _get(updateProgrammationQueryAtom);
    if (result.error) {
      set(notifyErrorAtom, {
        title: t('programmation:messages.programmation.updateError'),
      });
      return;
    }

    if (!programmationId) {
      return;
    }
  });

const updateProgrammationWithHistoryAtom = (t: TFunction<['programmation']>) =>
  atom<any, [Partial<Programmation>], any>(null, async (_get, set, params) => {
    const programmationId =
      params.id === undefined ? _get(progIdAtom) : params.id;
    const queryClient = _get(queryClientAtom);
    const { schoolyearId, schoolId } = _get(notNullSchoolyearSchoolAtom);
    const oldProgrammation = queryClient.getQueryData<Programmation>([
      schoolyearId,
      schoolId,
      PROGRAMMATION_KEY,
      programmationId,
    ]);
    await set(updateProgrammationAtom(t), params);
    if (!programmationId) {
      return;
    }
    const programmation = { ...params, id: programmationId };
    if (oldProgrammation) {
      set(addToPastAtom, {
        programmationId,
        actionHistory: {
          action: 'UPDATE_PROGRAMMATION',
          payload: {
            newProgrammation: { ...oldProgrammation, ...programmation },
            oldProgrammation,
          },
        },
      });
    }
    set(resetFutureAtom, programmationId);
  });

export const useUpdateProgrammationWithoutHistory = () => {
  const { t } = useTranslation(['programmation']);
  return useSetAtom(updateProgrammationAtom(t));
};
export const useUpdateProgrammation = () => {
  const { t } = useTranslation(['programmation']);
  return useSetAtom(updateProgrammationWithHistoryAtom(t));
};
